
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, favoriteProviderId?: string | null, useOtp?: boolean) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  verifySignupOtp: (email: string, token: string) => Promise<{ error: any }>;
  resendSignupOtp: (email: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithFacebook: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
  hasCompleteProfile: boolean;
  refreshSession: () => Promise<boolean>;
  sessionError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mobile Safari detection utility
const isMobileSafari = () => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const standalone = (window.navigator as any).standalone;
  return iOS && webkit && !standalone;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const isMobile = isMobileSafari();

  // Check if user has complete profile information
  const hasCompleteProfile = user ? 
    !!(user.user_metadata?.first_name && user.user_metadata?.last_name) : 
    false;

  console.log('=== AUTH PROVIDER DEBUG ===');
  console.log('Is Mobile Safari:', isMobile);
  console.log('Auth loading:', loading);
  console.log('User:', user?.email);
  console.log('Session exists:', !!session);
  console.log('Session valid:', session && new Date(session.expires_at! * 1000) > new Date());
  console.log('Session error:', sessionError);
  console.log('==========================');

  // Session refresh function
  const refreshSession = async (): Promise<boolean> => {
    try {
      console.log('Attempting to refresh session...');
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Session refresh failed:', error);
        setSessionError(error.message);
        return false;
      }
      
      if (data.session) {
        console.log('Session refreshed successfully');
        setSession(data.session);
        setUser(data.session.user);
        setSessionError(null);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Session refresh error:', error);
      setSessionError('Failed to refresh session');
      return false;
    }
  };

  // Session validation function
  const isSessionValid = (session: Session | null): boolean => {
    if (!session) return false;
    
    const now = new Date().getTime();
    const expiresAt = new Date(session.expires_at! * 1000).getTime();
    const timeUntilExpiry = expiresAt - now;
    
    // Consider session invalid if it expires within 5 minutes
    return timeUntilExpiry > 5 * 60 * 1000;
  };

  useEffect(() => {
    let mounted = true;
    console.log('Setting up auth state listener');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log("Auth state changed:", event, session?.user?.email);
        console.log("Session expires at:", session?.expires_at ? new Date(session.expires_at * 1000) : 'N/A');
        console.log("Is Mobile Safari:", isMobile);
        
        // Clear any previous errors
        setSessionError(null);
        
        // Handle different auth events
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log("User signed in or token refreshed");
          
          // Validate session before setting
          if (session && isSessionValid(session)) {
            setSession(session);
            setUser(session.user);
          } else if (session) {
            console.log("Session is expiring soon, attempting refresh...");
            const refreshed = await refreshSession();
            if (!refreshed) {
              setSession(null);
              setUser(null);
              setSessionError('Session expired');
            }
          }
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out");
          setSession(null);
          setUser(null);
          setSessionError(null);
        } else if (event === 'USER_UPDATED') {
          console.log("User updated");
          if (session) {
            setSession(session);
            setUser(session.user);
          }
        }
        
        // Add a small delay for mobile Safari to ensure state is properly set
        if (isMobile) {
          console.log("Mobile Safari detected, adding small delay before updating loading state");
          setTimeout(() => {
            if (mounted) setLoading(false);
          }, 50);
        } else {
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    console.log('Checking for existing session');
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (!mounted) return;
      
      console.log("Initial session check:", session?.user?.email);
      console.log("Session error:", error);
      
      if (error) {
        console.error("Session check error:", error);
        setSessionError(error.message);
        setLoading(false);
        return;
      }
      
      if (session) {
        // Validate the session
        if (isSessionValid(session)) {
          setSession(session);
          setUser(session.user);
        } else {
          console.log("Initial session is expired/expiring, attempting refresh...");
          const refreshed = await refreshSession();
          if (!refreshed) {
            setSession(null);
            setUser(null);
            setSessionError('Session expired on load');
          }
        }
      } else {
        setSession(null);
        setUser(null);
      }
      
      // Add a small delay for mobile Safari
      if (isMobile) {
        console.log("Mobile Safari detected, adding delay for initial session check");
        setTimeout(() => {
          if (mounted) setLoading(false);
        }, 100);
      } else {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      console.log('Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, [isMobile]);

  // Session monitoring - check every 5 minutes
  useEffect(() => {
    if (!session) return;
    
    const interval = setInterval(async () => {
      if (!isSessionValid(session)) {
        console.log("Session expired during monitoring, attempting refresh...");
        const refreshed = await refreshSession();
        if (!refreshed) {
          console.log("Failed to refresh expired session, signing out...");
          await signOut();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(interval);
  }, [session]);

  const signUp = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    favoriteProviderId?: string | null,
    useOtp?: boolean,
  ) => {
    // When useOtp is true (mobile/Capacitor app), omit emailRedirectTo so
    // Supabase sends a 6-digit OTP code instead of a magic link. The auth
    // email template must include {{ .Token }} for the code to appear.
    const redirectUrl = "https://wellsuites.co/auth";

    console.log("=== SIGNUP DEBUG ===");
    console.log("Email:", email, "OTP mode:", !!useOtp);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        ...(useOtp ? {} : { emailRedirectTo: redirectUrl }),
        data: {
          first_name: firstName,
          last_name: lastName,
          favorite_provider_id: favoriteProviderId || undefined,
        },
      },
    });

    if (!error) {
      localStorage.removeItem('pendingFavoriteProvider');
    }

    return { error };
  };

  const verifySignupOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { error };
  };

  const resendSignupOtp = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth`
      }
    });
    
    return { error };
  };

  const signInWithFacebook = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${window.location.origin}/auth`
      }
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSessionError(null);
  };

  const value = {
    user,
    session,
    signUp,
    signIn,
    verifySignupOtp,
    resendSignupOtp,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    loading,
    hasCompleteProfile,
    refreshSession,
    sessionError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
